import {v4 as uuidv4} from 'uuid';

import {
  validateEndSlideByCustomisationSetting,
  validateIntroSlide,
  validateLearningOutcomesSlide,
  validateResultsSlide,
  validateV4Activity,
} from '@edume/bento/lessonValidation';

import {QUESTION_TYPES} from '../../../enums/lessons';
import {deriveEditingType} from '../../../utils/editingContent';
import {
  DEFAULT_END_ICON,
  getQuestionTypeFromQuizType,
} from '../../../utils/lesson';
import * as slideUtils from './slideUtills';

export const DEFAULT_END_SLIDE_PARAMS = {
  title: 'Lesson complete',
  subtitle: 'Finish all the activities to complete this course.',
  icon: {
    url: DEFAULT_END_ICON,
  },
  button: {
    text: 'Next',
  },
  background: '#FFFFFF',
  fontColour: '#1E1E34',
  isLightButtonStyle: false,
};

function* idIterator() {
  let i = 0;
  while (true) {
    yield i++;
  }
}

export const iterator = idIterator();

export const isValidIntro = (intro) => validateIntroSlide(intro, false).result;
export const isValidLearningOutcomes = (learningOutcomes) =>
  validateLearningOutcomesSlide(learningOutcomes, false).result;
export const isValidEnd = (end) =>
  validateEndSlideByCustomisationSetting(end.endOfActivityCustomTextOptions)(
    end,
    false
  ).result;
export const isValidActivity = (activity) =>
  validateV4Activity(activity, false).result;

export const isValidResults = (results, minScore) =>
  validateResultsSlide(results, false, minScore).result;

export const getActiveSlideIndex = (slideElements, activeSlideElementIndex) =>
  slideElements[activeSlideElementIndex].slideIndex;

export const getActiveSlide = (
  slides,
  slideElements,
  activeSlideElementIndex
) => {
  const activeSlideIndex = getActiveSlideIndex(
    slideElements,
    activeSlideElementIndex
  );
  const activeSlide = slides[activeSlideIndex];
  return {
    activeSlide,
    activeSlideIndex,
  };
};

export const getFirstSlideElementIndexWithSlideIndex = (
  slideElements,
  slideIndex
) => slideElements.findIndex((element) => element.slideIndex === slideIndex);

export function cleanSingleResponseOptions(options) {
  const firstCorrectOptionIndex = options.findIndex(
    (option) => option.isCorrect
  );
  const newCorrectOptionIndex =
    firstCorrectOptionIndex !== -1 ? firstCorrectOptionIndex : 0;

  return options.map((option, index) => ({
    ...option,
    isCorrect: index === newCorrectOptionIndex,
  }));
}

export function cleanMultipleResponseOptions(options) {
  if (options.every((opt) => typeof opt?.isCorrect === 'boolean')) {
    return options;
  }

  return cleanSingleResponseOptions(options);
}

export function cleanOrderingOptions(options) {
  const MINIMUM_ORDERING_OPTIONS = 3;
  const MAXIMUM_ORDERING_OPTIONS = 5;
  if (options.length < MINIMUM_ORDERING_OPTIONS) {
    const numToAdd = 3 - options.length;
    const newOptions = Array.from({length: numToAdd}, () => ({text: ''}));
    return [...options, ...newOptions];
  }
  return options.filter((_, idx) => idx < MAXIMUM_ORDERING_OPTIONS);
}

export const emptyQuestion = {
  question: '',
  options: [
    {text: '', isCorrect: true},
    {text: '', isCorrect: false},
  ],
};

export function cleanQuestionOptionsByType(type, options) {
  switch (type) {
    case QUESTION_TYPES.SINGLE_RESPONSE:
      return cleanSingleResponseOptions(options);
    case QUESTION_TYPES.MULTIPLE_RESPONSE:
      return cleanMultipleResponseOptions(options);
    case QUESTION_TYPES.ORDERING:
      return cleanOrderingOptions(options);
    default:
      return options;
  }
}

export const createMixedQuizMultipleChoiceQuestion = (
  prompt,
  type = QUESTION_TYPES.SINGLE_RESPONSE,
  baseQuestion = emptyQuestion
) => ({
  ...baseQuestion,
  type,
  prompt,
});

export const convertToMixedQuizMultipleChoiceQuestion =
  (prompt, type) => (question) =>
    createMixedQuizMultipleChoiceQuestion(prompt, type, question);

export function convertToMixedQuizType(params) {
  const {prompt, quizType} = params;

  if (quizType === 'text') {
    const {answers, questionId, ...paramsRest} = params;
    return {
      ...paramsRest,
      prompt: null,
      shuffleAnswers: true,
      answers: answers.map(() => null),
      quizType: 'mixed',
      questions: [
        {
          id: questionId,
          question: prompt,
          prompt: '',
          options: answers.map((text) => ({text})),
          type: QUESTION_TYPES.ORDERING,
        },
      ],
    };
  }
  const {questions, ...paramsRest} = params;

  const questionType = getQuestionTypeFromQuizType(quizType);
  return {
    ...paramsRest,
    prompt: null,
    quizType: 'mixed',
    questions: questions.map(
      convertToMixedQuizMultipleChoiceQuestion(prompt, questionType)
    ),
  };
}

export function convertToInteractionWithMixedQuizType(interaction) {
  const type =
    interaction.type === 'ordering' ? 'standaloneQuiz' : interaction.type;
  return {
    type,
    params: convertToMixedQuizType(interaction.params),
  };
}

const KEYS_EXCLUDED_FROM_DUPLICATION = [
  'id',
  'questionId',
  'slideId',
  'lessonKey',
  'createdAt',
  'updatedAt',
  'slideNumber',
  'index',
];

export const isKeyExcludedFromDuplication = (key) =>
  key.endsWith('Ckey') ||
  key.endsWith('Id') ||
  KEYS_EXCLUDED_FROM_DUPLICATION.includes(key);

export const deleteExcludedKeysFromDuplicatedSlide = (obj) => {
  if (!obj) {
    return;
  }
  Object.keys(obj).forEach((key) => {
    if (isKeyExcludedFromDuplication(key)) {
      delete obj[key];
    } else if (typeof obj[key] === 'object') {
      deleteExcludedKeysFromDuplicatedSlide(obj[key]);
    }
  });
};

export const normaliseOverlayElementOrder = (elements) => {
  if (!elements) {
    return [];
  }
  return elements
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((el, index) => ({...el, order: index + 1}));
};

export const setOverlayElementToTop = (
  currentElements,
  elementType,
  updates = null
) => {
  let topElementCandidate;
  const otherElements = [];

  currentElements.forEach((el) => {
    if (el.type === elementType) {
      topElementCandidate = {...el, ...(updates || {})};
    } else {
      otherElements.push(el);
    }
  });

  if (!topElementCandidate) {
    return currentElements;
  }

  const normalizedOtherElements = normaliseOverlayElementOrder(otherElements);

  topElementCandidate.order = normalizedOtherElements.length + 1;

  return [...normalizedOtherElements, topElementCandidate];
};

// Add a temporary client-side ID so that elements behave predictably in scenarios where IDs are needed,
// such as when mapping.
// This ID is stripped out on the server and replaced with a DB generated ID.
export const addClientSideIdToOverlayElements = (overlayElements) =>
  overlayElements.map((element) => ({
    id: uuidv4(),
    ...element,
  }));

export const validateSlideByType = (slide) => {
  switch (slide.type) {
    case 'intro':
      return isValidIntro(slide.params);
    case 'learningOutcomes':
      return isValidLearningOutcomes(slide.params);
    case 'end':
      return isValidEnd(slide.params);
    default:
      return isValidActivity(slide);
  }
};

// eslint-disable-next-line complexity
export const fromEsonToState = (
  lesson,
  shouldCreateIds = true,
  activeSlideElementIndex
) => {
  const {eson} = lesson;
  const isAssessment = eson.type === 'assessment';

  const {
    intro,
    learningOutcomes,
    activities = [],
    results,
    end,
    endOfActivityCustomTextOptions,
  } = eson.params;

  let slides = [];
  let slideReadyStates;
  let minimumScore;

  if (isAssessment) {
    minimumScore = eson.params.minimumScore;

    slides = [
      {
        type: 'intro',
        params: intro,
        ...(shouldCreateIds && {id: iterator.next().value}),
      },
      ...activities.map((a) => ({
        ...a,
        ...(shouldCreateIds && {id: iterator.next().value}),
      })),
      {
        type: 'results',
        params: results,
        ...(shouldCreateIds && {id: iterator.next().value}),
        minimumScore,
        isPassing: true,
      },
    ];

    slideReadyStates = [
      isValidIntro(intro),
      ...activities.map(isValidActivity),
      isValidResults(results, minimumScore),
    ];
  } else {
    if (intro) {
      slides.push({
        type: 'intro',
        params: intro,
        ...(shouldCreateIds && {id: iterator.next().value}),
      });
    }
    if (learningOutcomes) {
      slides.push({
        type: 'learningOutcomes',
        params: learningOutcomes,
        ...(shouldCreateIds && {id: iterator.next().value}),
      });
    }
    slides.push(
      ...activities.map((a) => ({
        ...a,
        ...(shouldCreateIds && {id: iterator.next().value}),
      }))
    );

    const endParams = end || DEFAULT_END_SLIDE_PARAMS;
    endParams.endOfActivityCustomTextOptions = endOfActivityCustomTextOptions;
    if (!endOfActivityCustomTextOptions) {
      endParams.title ??= '';
      endParams.subtitle ??= '';
      endParams.button ??= {text: ''};
    }

    slides.push({
      type: 'end',
      params: endParams,
      ...(shouldCreateIds && {id: iterator.next().value}),
    });

    slideReadyStates = slides.map(validateSlideByType);
  }
  const editingType = deriveEditingType(lesson);

  const slideElements = slides
    .map(slideUtils.getSlideElements(editingType, isAssessment))
    .flat();

  return {
    activeSlideElementIndex: activeSlideElementIndex,
    slides,
    slideElements,
    slideReadyStates,
    isAssessment,
    minimumScore,
    unsavedChanges: false,
    editingType,
  };
};
